/*
 * <unik-menu-hover>: the desktop mega menu (F6-03), after the Shella reference
 * (Menu._toggleMegamenu in assets/section.header.js of shella-dev-final). A top-level item with
 * a panel (li.unik-menu-item) is a real link to its collection plus a keyboard button, rendered
 * by snippets/header-mega-menu.liquid. This element opens and closes the panels:
 *
 * - Hover, with a pointer that can hover, from 750 px (the desktop header). A short delay keeps
 *   panels from flashing while the pointer crosses the menu row; leaving the item and its panel
 *   closes it.
 * - The button (shown on keyboard focus) toggles the panel. Escape closes it and returns focus to
 *   the button; focus leaving the item closes it.
 * - Touch: the first tap on the link opens the panel, the next one follows the link.
 *
 * Moving from one top-level item to another swaps the panel without sliding it in again
 * (is-switching); its height glides from the previous panel's to the new one, like the
 * reference's curtain. While a panel is open the sticky header does not hide (preventHide, as
 * Colorblock's header-menu did). Colorblock's <header-menu> <details> is not used: Safari lost
 * the hand cursor over its summaries once they had been opened and closed on hover.
 */
if (!customElements.get('unik-menu-hover')) {
  class UnikMenuHover extends HTMLElement {
    static OPEN_DELAY = 80;
    static CLOSE_DELAY = 120;
    static RESIZE_DURATION = 200;
    static MEDIA_QUERY = '(hover: hover) and (pointer: fine) and (min-width: 750px)';

    connectedCallback() {
      if (this.controller) return;
      this.nav = this.parentElement;
      if (!this.nav) return;

      this.header = this.closest('.header-wrapper');
      this.media = window.matchMedia(UnikMenuHover.MEDIA_QUERY);
      this.controller = new AbortController();
      const { signal } = this.controller;

      this.items.forEach((item) => {
        item.addEventListener('pointerenter', (event) => this.onEnter(item, event), { signal });
        item.addEventListener('pointerleave', (event) => this.onLeave(event), { signal });
        item.addEventListener('pointerdown', (event) => (this.pointerType = event.pointerType), { signal });
        item.addEventListener('keydown', (event) => this.onKeydown(item, event), { signal });
        item.addEventListener('focusout', (event) => this.onFocusOut(item, event), { signal });
        this.linkOf(item)?.addEventListener('click', (event) => this.onLinkClick(item, event), { signal });
        this.toggleOf(item)?.addEventListener('click', () => this.toggle(item), { signal });
      });

      this.media.addEventListener(
        'change',
        () => {
          if (!this.media.matches) this.closeAll();
        },
        { signal }
      );
    }

    disconnectedCallback() {
      this.clearTimers();
      this.controller?.abort();
      this.controller = null;
    }

    get items() {
      return [...this.nav.querySelectorAll('.unik-menu-item')];
    }

    /** Hover applies to a mouse (or pen) on a large screen, never to touch. */
    isHover(event) {
      return this.media.matches && event.pointerType !== 'touch';
    }

    isOpen(item) {
      return item.classList.contains('is-open');
    }

    onEnter(item, event) {
      if (!this.isHover(event)) return;
      this.hovered = item;
      this.clearTimers();
      if (this.isOpen(item)) return;
      this.openTimer = window.setTimeout(() => this.open(item), UnikMenuHover.OPEN_DELAY);
    }

    onLeave(event) {
      if (!this.isHover(event)) return;
      this.hovered = null;
      this.clearTimers();
      this.closeTimer = window.setTimeout(() => this.closeAll(), UnikMenuHover.CLOSE_DELAY);
    }

    /** Touch: the first tap opens the panel, the next one follows the link. */
    onLinkClick(item, event) {
      const touch = !this.media.matches || this.pointerType === 'touch';
      if (event.detail === 0 || !touch || this.isOpen(item)) return;
      event.preventDefault();
      this.open(item);
    }

    onKeydown(item, event) {
      if (event.key !== 'Escape' || !this.isOpen(item)) return;
      event.preventDefault();
      event.stopPropagation();
      const fromLink = document.activeElement === this.linkOf(item);
      this.close(item);
      this.syncHeader();
      if (!fromLink) this.toggleOf(item)?.focus();
    }

    /** Focus leaving the item closes its panel, unless the pointer still holds it open. */
    onFocusOut(item, event) {
      if (!this.isOpen(item) || item.contains(event.relatedTarget) || this.hovered === item) return;
      this.close(item);
      this.syncHeader();
    }

    toggle(item) {
      this.clearTimers();
      if (this.isOpen(item)) {
        this.close(item);
        this.syncHeader();
      } else {
        this.open(item);
      }
    }

    open(item) {
      const others = this.items.filter((other) => other !== item && this.isOpen(other));
      const previousHeight = others.length ? this.layerOf(others[0])?.getBoundingClientRect().height : 0;
      item.classList.toggle('is-switching', others.length > 0);
      others.forEach((other) => this.close(other));
      item.classList.add('is-open');
      this.panelOf(item).hidden = false;
      this.toggleOf(item)?.setAttribute('aria-expanded', 'true');
      if (this.header) this.header.preventHide = true;
      if (previousHeight) this.resize(this.layerOf(item), previousHeight);
    }

    /** Glides the swapped-in panel from the previous panel's height to its own. */
    resize(layer, fromHeight) {
      this.resizeAnimation?.cancel();
      if (!layer || typeof layer.animate !== 'function') return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const toHeight = layer.getBoundingClientRect().height;
      if (!toHeight || Math.abs(toHeight - fromHeight) < 1) return;

      layer.style.overflow = 'hidden';
      this.resizeAnimation = layer.animate([{ height: `${fromHeight}px` }, { height: `${toHeight}px` }], {
        duration: UnikMenuHover.RESIZE_DURATION,
        easing: 'cubic-bezier(0.2, 0, 0, 1)',
      });
      const reset = () => {
        layer.style.overflow = '';
      };
      // The promise settles as soon as the animation ends or is cancelled; the finish event
      // waits for the next rendered frame.
      this.resizeAnimation.finished.then(reset, reset);
    }

    close(item) {
      item.classList.remove('is-open', 'is-switching');
      this.panelOf(item).hidden = true;
      this.toggleOf(item)?.setAttribute('aria-expanded', 'false');
    }

    closeAll() {
      this.clearTimers();
      this.resizeAnimation?.cancel();
      this.items.filter((item) => this.isOpen(item)).forEach((item) => this.close(item));
      this.syncHeader();
    }

    /** The sticky header stays while a panel is open. */
    syncHeader() {
      if (this.header) this.header.preventHide = this.items.some((item) => this.isOpen(item));
    }

    clearTimers() {
      window.clearTimeout(this.openTimer);
      window.clearTimeout(this.closeTimer);
    }

    linkOf(item) {
      return item.querySelector('.header__menu-item');
    }

    toggleOf(item) {
      return item.querySelector('.unik-menu-toggle');
    }

    panelOf(item) {
      return item.querySelector('.mega-menu__content');
    }

    layerOf(item) {
      return item.querySelector('.unik-megamenu-panel');
    }
  }

  customElements.define('unik-menu-hover', UnikMenuHover);
}
